import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ChevronDown, Crown, LogOut, BookOpen } from 'lucide-react'
import { useAuth } from '../hooks/useAuth.jsx'
import { sage, neutral, gold, fonts, shadows } from '../styles/theme.js'

export default function BookCover() {
  const { user, signOut, isSubscriber } = useAuth()
  const navigate = useNavigate()
  const [isOpening, setIsOpening] = useState(false)

  const handleOpen = () => {
    setIsOpening(true)
    setTimeout(() => navigate('/recipes'), 500)
  }

  return (
    <div style={{
      minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
      background: `linear-gradient(150deg, ${sage.faint} 0%, #ffffff 60%, ${sage.pale} 100%)`,
      padding: 24, position: 'relative', overflow: 'hidden',
    }}>
      {/* Subtle background texture */}
      <div style={{
        position: 'absolute', inset: 0, opacity: 0.015,
        backgroundImage: `radial-gradient(circle, ${sage.dark} 1px, transparent 1px)`,
        backgroundSize: '28px 28px',
      }} />

      {/* User chip */}
      <UserChip user={user} isSubscriber={isSubscriber} onSignOut={signOut} />

      {/* Book */}
      <div
        onClick={handleOpen}
        style={{
          width: 340, maxWidth: '88vw',
          aspectRatio: '3/4',
          background: '#ffffff',
          borderRadius: '6px 16px 16px 6px',
          boxShadow: isOpening
            ? '24px 12px 60px rgba(0,0,0,0.18), -4px 0 16px rgba(0,0,0,0.08)'
            : `${shadows.book}`,
          position: 'relative', cursor: 'pointer',
          transform: isOpening ? 'perspective(1400px) rotateY(-22deg) scale(1.02)' : 'perspective(1400px) rotateY(-2deg)',
          transition: 'transform 0.45s ease, box-shadow 0.45s ease',
          userSelect: 'none', border: `1px solid ${neutral[300]}`,
        }}
      >
        {/* Spine */}
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0, width: 20,
          background: `linear-gradient(90deg, ${sage.dark}, ${sage.mid})`,
          borderRadius: '6px 0 0 6px',
        }} />

        {/* Spine accent line */}
        <div style={{
          position: 'absolute', left: 20, top: 0, bottom: 0, width: 3,
          background: `linear-gradient(180deg, ${sage.pale}, ${sage.pale}00)`,
          opacity: 0.4,
        }} />

        {/* Top colour band */}
        <div style={{
          position: 'absolute', top: 0, left: 20, right: 0, height: 8,
          background: sage.mid, borderRadius: '0 16px 0 0',
        }} />
        <div style={{
          position: 'absolute', bottom: 0, left: 20, right: 0, height: 8,
          background: sage.mid, borderRadius: '0 0 16px 0',
        }} />

        {/* Main content area */}
        <div style={{
          position: 'absolute', top: 8, left: 23, right: 0, bottom: 8,
          display: 'flex', flexDirection: 'column',
          alignItems: 'center', justifyContent: 'center',
          padding: '32px 28px',
        }}>
          {/* Logo mark */}
          <div style={{
            width: 72, height: 72, borderRadius: '50%',
            background: sage.pale, border: `2px solid ${sage.pale === '#f2faf6' ? sage.light : sage.pale}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            marginBottom: 24, boxShadow: '0 2px 12px rgba(77,139,111,0.15)',
          }}>
            <BookOpen size={34} color={sage.mid} />
          </div>

          {/* Title */}
          <h1 style={{
            fontFamily: fonts.cursive, fontSize: 42, fontWeight: 700,
            color: sage.dark, textAlign: 'center', lineHeight: 1.1,
            marginBottom: 8, letterSpacing: '-0.5px',
          }}>
            My Cookbook
          </h1>

          {/* Thin divider */}
          <div style={{
            width: 48, height: 2, background: sage.mid,
            borderRadius: 1, margin: '16px auto 16px',
            opacity: 0.5,
          }} />

          <p style={{
            fontFamily: fonts.cursive, fontSize: 17, fontStyle: 'italic',
            color: sage.light, textAlign: 'center', lineHeight: 1.5,
          }}>
            A family recipe collection
          </p>

          {/* Page edge lines — decorative */}
          <div style={{
            position: 'absolute', right: 6, top: 28, bottom: 28,
            display: 'flex', flexDirection: 'column', gap: 2, justifyContent: 'center',
          }}>
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} style={{
                width: 6, height: 1,
                background: neutral[300], opacity: 0.6,
              }} />
            ))}
          </div>
        </div>

        {/* Tap prompt */}
        <div style={{
          position: 'absolute', bottom: 32, left: 23, right: 0,
          display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
        }}>
          <p style={{
            fontFamily: fonts.serif, fontSize: 12, color: neutral[500],
            letterSpacing: 2, textTransform: 'uppercase', fontWeight: 500,
          }}>
            Open
          </p>
          <ChevronDown
            size={16} color={neutral[500]}
            style={{ animation: 'bounce 1.6s ease-in-out infinite' }}
          />
        </div>
      </div>
    </div>
  )
}

function UserChip({ user, isSubscriber, onSignOut }) {
  const [open, setOpen] = useState(false)

  return (
    <div style={{ position: 'fixed', top: 20, right: 20, zIndex: 100 }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
          background: '#ffffffee', border: `1px solid ${neutral[300]}`,
          borderRadius: 24, cursor: 'pointer', backdropFilter: 'blur(8px)',
          boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
        }}
      >
        {user?.avatar_url ? (
          <img src={user.avatar_url} alt="" style={{ width: 26, height: 26, borderRadius: '50%' }} />
        ) : (
          <div style={{
            width: 26, height: 26, borderRadius: '50%', background: sage.mid,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 12, fontWeight: 600, color: 'white',
          }}>
            {user?.name?.[0]?.toUpperCase() || '?'}
          </div>
        )}
        <span style={{ fontFamily: fonts.serif, fontSize: 14, color: neutral[700], fontWeight: 500 }}>
          {user?.name?.split(' ')[0]}
        </span>
        {isSubscriber && <Crown size={13} color={gold.mid} />}
        <ChevronDown size={13} color={neutral[500]} style={{ transform: open ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: '100%', right: 0, marginTop: 6,
          background: '#ffffff', border: `1px solid ${neutral[300]}`,
          borderRadius: 10, overflow: 'hidden', minWidth: 180,
          boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
        }}>
          <div style={{ padding: '10px 14px', borderBottom: `1px solid ${neutral[100]}` }}>
            <p style={{ fontFamily: fonts.serif, fontSize: 13, color: neutral[700] }}>{user?.email}</p>
            <p style={{ fontFamily: fonts.serif, fontSize: 12, color: neutral[500], marginTop: 2 }}>
              {isSubscriber ? '✦ Subscriber' : 'Free tier'}
            </p>
          </div>
          <button
            onClick={onSignOut}
            style={{
              width: '100%', padding: '10px 14px', background: 'none', border: 'none',
              cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8,
              fontFamily: fonts.serif, fontSize: 14, color: neutral[700], textAlign: 'left',
            }}
          >
            <LogOut size={14} /> Sign Out
          </button>
        </div>
      )}
    </div>
  )
}
